// The Washing Plan's printed schedule and Excel file, in English, Greek or both (some of the people who wash
// the boats don't read English). The printout is A4 landscape:
//   page 1 — the month as a calendar, Monday first, each wash a chip in its colour with the boat's name and
//            SAP (a tick once done), each day's washing time, the four washes named in a key;
//   page 2 on — day by day, a row per wash: the boat, SAP, model, which wash and how long it takes (the
//            outside and the interior, by the model), its delivery, the note, and a box to tick and sign by
//            hand; each day's total under its date.
// Days and months are named by the browser in each language ("Πέμπτη 15 Οκτωβρίου"), never shifted through
// a time zone. Opened from a click with printInWindow (the app's CSP keeps scripts out of the window).
import { WASH_KINDS, WASH_KIND_INFO, sumWashTimes, washTime, type WashKind, type WashMinutes, type WashRecord } from '@technohull/shared';
import { printInWindow } from './printWindow.js';
import { washRangeText } from './washPlan.js';
import { monthWeeks } from './serviceCalendar.js';

export type WashLang = 'both' | 'el' | 'en';

/** one wash on the schedule */
export interface WashLine {
  day: string;
  kind: WashKind;
  boat: string;
  sap: string;
  model: string;
  delivery: string;
  note: string;
  done: boolean;
}

const esc = (s: string) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
const utc = (day: string) => new Date(`${day}T00:00:00Z`);
const fmt = (locale: string, o: Intl.DateTimeFormatOptions) => (day: string) => new Intl.DateTimeFormat(locale, { ...o, timeZone: 'UTC' }).format(utc(day));
const monthEn = fmt('en-GB', { month: 'long', year: 'numeric' });
const monthEl = fmt('el-GR', { month: 'long', year: 'numeric' });
const dayEn = fmt('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
const dayEl = fmt('el-GR', { weekday: 'long', day: 'numeric', month: 'long' });
const shortEn = fmt('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const shortEl = fmt('el-GR', { day: 'numeric', month: 'short', year: 'numeric' });
const WEEK = ['2026-10-12', '2026-10-13', '2026-10-14', '2026-10-15', '2026-10-16', '2026-10-17', '2026-10-18']; // a Monday-first week
const wdEn = fmt('en-GB', { weekday: 'long' });
const wdEl = fmt('el-GR', { weekday: 'long' });

/** a label in the chosen language(s): "Boat · Σκάφος" for both. */
export const pick = (lang: WashLang, en: string, el: string, sep = ' · ') => (lang === 'en' ? en : lang === 'el' ? el : `${en}${sep}${el}`);

/** the printout's words, English and Greek */
export const WASH_WORDS = {
  title: ['Washing plan', 'Πρόγραμμα πλυσίματος'],
  date: ['Date', 'Ημερομηνία'],
  boat: ['Boat', 'Σκάφος'],
  model: ['Model', 'Μοντέλο'],
  wash: ['Wash', 'Πλύσιμο'],
  delivery: ['Delivery', 'Παράδοση'],
  note: ['Note', 'Σημείωση'],
  done: ['Done', 'Έγινε'],
  sign: ['Signature', 'Υπογραφή'],
  printed: ['Printed', 'Εκτύπωση'],
  washes: ['washes', 'πλυσίματα'],
  outside: ['Outside', 'Εξωτερικό'],
  interior: ['Interior', 'Εσωτερικό'],
  total: ['Washing time', 'Χρόνος πλυσίματος'],
  none: ['No washes planned this month.', 'Δεν έχουν προγραμματιστεί πλυσίματα αυτόν τον μήνα.'],
} as const;
const W = (lang: WashLang, k: keyof typeof WASH_WORDS, sep?: string) => pick(lang, WASH_WORDS[k][0], WASH_WORDS[k][1], sep);

// each wash's print colour (the page's own, as hex so it prints the same everywhere)
export const WASH_PRINT_COLOUR: Record<WashKind, string> = { seatrial: '#2f6fd6', predelivery: '#7a4fd0', rewash: '#c27a0e', service: '#14866d' };

/** the month's washes as schedule lines, from the plan (planned or done in that month), by day then wash.
 *  `model` reads the boat's model where the sheets know it better than the record. */
export function washLines(
  records: readonly WashRecord[], month: string, delivery: (r: WashRecord) => string, sap: (r: WashRecord) => string,
  model: (r: WashRecord) => string = (r) => r.boatModel,
): WashLine[] {
  return records
    .filter((r) => (r.doneOn || r.plannedOn).startsWith(month) && (r.doneOn || r.plannedOn))
    .map((r) => ({
      day: r.doneOn || r.plannedOn, kind: r.kind, boat: r.boatName || r.boatKey, sap: sap(r), model: model(r),
      delivery: delivery(r), note: r.note, done: !!r.doneOn,
    }))
    .sort((a, b) => a.day.localeCompare(b.day) || WASH_KINDS.indexOf(a.kind) - WASH_KINDS.indexOf(b.kind) || a.boat.localeCompare(b.boat));
}

/** the printed schedule's page, as HTML. `today` stamps it. */
export function washScheduleHtml(lines: readonly WashLine[], month: string, lang: WashLang, today: string): string {
  const title = W(lang, 'title');
  const monthName = pick(lang, monthEn(`${month}-01`), monthEl(`${month}-01`));
  const logo = `${window.location.origin}/technohull-logo-doc.png`;
  const kindName = (k: WashKind, sep = '<br>') => pick(lang, esc(WASH_KIND_INFO[k].name), `<span lang="el">${esc(WASH_KIND_INFO[k].nameEl)}</span>`, sep);
  const byDay = new Map<string, WashLine[]>();
  for (const l of lines) byDay.set(l.day, [...(byDay.get(l.day) ?? []), l]);
  // how long, in the language picked (hours and minutes in Greek units for a Greek-only schedule)
  const tt = (r: WashMinutes) => washRangeText(r, lang === 'el');
  const dayTime = (day: string) => {
    const t = sumWashTimes((byDay.get(day) ?? []).map((l) => l.model));
    return t.counted ? `${tt(t.total)}${t.unknown ? '+' : ''}` : '';
  };

  const legend = WASH_KINDS.map((k) => `<span class="key"><i style="background:${WASH_PRINT_COLOUR[k]}">${WASH_KIND_INFO[k].n}</i>${kindName(k, ' · ')}</span>`).join('');
  const head = WEEK.map((d) => `<th>${esc(pick(lang, wdEn(d), wdEl(d), ' / '))}</th>`).join('');
  const weeks = monthWeeks(month).map((week) => `<tr>${week.map((d) => {
    const inMonth = d.startsWith(month);
    const items = (byDay.get(d) ?? []).map((l) => `<div class="chip${l.done ? ' done' : ''}" style="border-color:${WASH_PRINT_COLOUR[l.kind]}"><i style="background:${WASH_PRINT_COLOUR[l.kind]}">${WASH_KIND_INFO[l.kind].n}</i><b>${esc(l.boat)}</b>${l.sap ? ` <small>${esc(l.sap)}</small>` : ''}${l.done ? ' <span class="tick">✓</span>' : ''}</div>`).join('');
    const time = inMonth ? dayTime(d) : '';
    return `<td class="${inMonth ? '' : 'out'}${d === today ? ' today' : ''}"><div class="dn">${Number(d.slice(8))}${time ? `<span class="dt">⏱ ${esc(time)}</span>` : ''}</div>${inMonth ? items : ''}</td>`;
  }).join('')}</tr>`).join('');

  const rows = lines.map((l, i) => {
    const first = i === 0 || lines[i - 1]!.day !== l.day;
    const total = first ? dayTime(l.day) : '';
    const date = first
      ? pick(lang, esc(dayEn(l.day)), `<span lang="el">${esc(dayEl(l.day))}</span>`, '<br>') + (total ? `<div class="tot">⏱ ${esc(W(lang, 'total', ' · '))}: <b>${esc(total)}</b></div>` : '')
      : '';
    const t = washTime(l.model);
    const time = t ? `<div class="tm">${esc(W(lang, 'outside', ' · '))}: <b>${esc(tt(t.outside))}</b><br>${esc(W(lang, 'interior', ' · '))}: <b>${esc(tt(t.interior))}</b></div>` : '';
    const del = l.delivery ? pick(lang, shortEn(l.delivery), shortEl(l.delivery), '<br>') : '–';
    return `<tr class="${first ? 'first' : ''}${l.done ? ' isdone' : ''}">
      <td class="d">${date}</td>
      <td><b>${esc(l.boat)}</b></td><td>${esc(l.sap)}</td><td>${esc(l.model)}</td>
      <td><span class="k" style="background:${WASH_PRINT_COLOUR[l.kind]}">${WASH_KIND_INFO[l.kind].n}</span> ${kindName(l.kind)}${time}</td>
      <td>${del}</td><td>${esc(l.note)}</td>
      <td class="box">${l.done ? '✓' : ''}</td><td class="sig"></td>
    </tr>`;
  }).join('');
  const list = lines.length
    ? `<table class="list"><thead><tr>
        <th style="width:15%">${W(lang, 'date', '<br>')}</th><th style="width:17%">${W(lang, 'boat', '<br>')}</th><th style="width:7%">SAP</th>
        <th style="width:7%">${W(lang, 'model', '<br>')}</th><th style="width:21%">${W(lang, 'wash', '<br>')}</th><th style="width:9%">${W(lang, 'delivery', '<br>')}</th>
        <th>${W(lang, 'note', '<br>')}</th><th style="width:5%">${W(lang, 'done', '<br>')}</th><th style="width:9%">${W(lang, 'sign', '<br>')}</th>
      </tr></thead><tbody>${rows}</tbody></table>`
    : `<p class="none">${esc(W(lang, 'none', ' / '))}</p>`;

  return `<!doctype html><html lang="${lang === 'el' ? 'el' : 'en'}"><head><meta charset="utf-8"><title>${esc(`${title} — ${monthName}`)}</title><style>
    @page{size:A4 landscape;margin:9mm}
    *{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    body{margin:0;font-family:Arial,Helvetica,sans-serif;color:#141821;font-size:10px}
    .hd{display:flex;align-items:center;gap:14px;padding-bottom:7px;border-bottom:2px solid #141821;margin-bottom:8px}
    .hd img{height:7mm}
    .hd h1{margin:0;font-size:18px;letter-spacing:.2px}
    .hd .m{margin-left:auto;text-align:right}
    .hd .m b{display:block;font-size:16px}
    .hd .m small{color:#667}
    .legend{display:flex;flex-wrap:wrap;gap:6px 16px;margin:0 0 7px;font-size:9.5px}
    .key{display:inline-flex;align-items:center;gap:5px}
    i{display:inline-grid;place-items:center;width:13px;height:13px;border-radius:50%;color:#fff;font-style:normal;font-weight:700;font-size:8px;flex:none}
    table{border-collapse:collapse;width:100%;table-layout:fixed}
    .cal th{background:#141821;color:#fff;padding:4px;font-size:9px;font-weight:600;text-align:left}
    .cal td{border:1px solid #c9ced8;vertical-align:top;height:24mm;padding:3px}
    .cal td.out{background:#f1f2f5}
    .cal td.today{box-shadow:inset 0 0 0 2px #141821}
    .dn{display:flex;align-items:baseline;gap:4px;font-weight:700;font-size:11px;margin-bottom:2px}
    .dn .dt{margin-left:auto;font-size:8px;font-weight:600;color:#445;white-space:nowrap}
    .chip{display:flex;align-items:center;gap:4px;margin:0 0 2px;padding:2px 4px;border-left:3px solid;background:#f7f8fa;border-radius:2px;font-size:8.5px;line-height:1.2;overflow:hidden}
    .chip b{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .chip small{color:#667;white-space:nowrap}
    .chip.done{opacity:.6}
    .tick{color:#14866d;font-weight:700}
    .list{margin-top:2px;break-before:page;page-break-before:always}
    .list th{background:#141821;color:#fff;padding:5px;font-size:9px;text-align:left;vertical-align:bottom}
    .list td{border-bottom:1px solid #d6d9e0;padding:5px;vertical-align:top;font-size:9.5px}
    .list tr.first td{border-top:2px solid #141821}
    .list td.d{font-weight:700}
    .list .tot{margin-top:4px;font-weight:600;font-size:8.5px;color:#334}
    .list .tot b{white-space:nowrap;font-size:9.5px;color:#141821}
    .list .tm{margin-top:3px;font-size:8.5px;color:#445;line-height:1.35}
    .list tr.isdone td{color:#556}
    .list .k{display:inline-grid;place-items:center;width:14px;height:14px;border-radius:50%;color:#fff;font-weight:700;font-size:8px;vertical-align:middle}
    .list td.box{text-align:center;font-size:13px;font-weight:700;color:#14866d}
    .list td.box:empty::before{content:'';display:inline-block;width:12px;height:12px;border:1.5px solid #141821;border-radius:2px}
    .list thead{display:table-header-group}
    .list tr{break-inside:avoid;page-break-inside:avoid}
    .none{font-size:12px;color:#556}
    [lang=el]{font-style:normal}
  </style></head><body>
    <div class="hd"><img src="${logo}" alt="TECHNOHULL"><h1>${esc(title)}</h1>
      <div class="m"><b>${esc(monthName)}</b><small>${lines.length} ${esc(W(lang, 'washes', ' / '))} · ${esc(W(lang, 'printed', ' / '))} ${esc(pick(lang, shortEn(today), shortEl(today), ' / '))}</small></div></div>
    <div class="legend">${legend}</div>
    <table class="cal"><thead><tr>${head}</tr></thead><tbody>${weeks}</tbody></table>
    ${list}
  </body></html>`;
}

/** print the month's schedule (from a click). False when the pop-up was blocked. */
export function printWashSchedule(lines: readonly WashLine[], month: string, lang: WashLang, today: string): boolean {
  return printInWindow(washScheduleHtml(lines, month, lang, today), undefined, 'width=1200,height=850');
}

/** the month's schedule as an Excel file, its headings and wash names in the chosen language(s). */
export async function exportWashScheduleXlsx(lines: readonly WashLine[], month: string, lang: WashLang): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const XLSX: any = await import('xlsx');
  const head = [
    W(lang, 'date', ' / '), W(lang, 'boat', ' / '), 'SAP', W(lang, 'model', ' / '), W(lang, 'wash', ' / '),
    W(lang, 'outside', ' / '), W(lang, 'interior', ' / '), W(lang, 'delivery', ' / '), W(lang, 'note', ' / '), W(lang, 'done', ' / '),
  ];
  const day = (d: string) => (d ? pick(lang, dayEn(d), dayEl(d), ' / ') : '');
  const tt = (r: WashMinutes) => washRangeText(r, lang === 'el');
  const body = lines.map((l) => {
    const t = washTime(l.model);
    return [
      day(l.day), l.boat, l.sap, l.model, pick(lang, WASH_KIND_INFO[l.kind].name, WASH_KIND_INFO[l.kind].nameEl, ' / '),
      t ? tt(t.outside) : '', t ? tt(t.interior) : '',
      l.delivery ? pick(lang, shortEn(l.delivery), shortEl(l.delivery), ' / ') : '', l.note, l.done ? '✓' : '',
    ];
  });
  const ws = XLSX.utils.aoa_to_sheet([head, ...body]);
  ws['!cols'] = [{ wch: 34 }, { wch: 30 }, { wch: 11 }, { wch: 10 }, { wch: 52 }, { wch: 16 }, { wch: 16 }, { wch: 22 }, { wch: 30 }, { wch: 8 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, lang === 'el' ? 'Πλυσίματα' : 'Washing plan');
  const filename = `${lang === 'el' ? 'Πρόγραμμα πλυσίματος' : 'Washing plan'} ${month}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}
